import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Plus, Minus, RefreshCw, DollarSign, PencilLine, CheckCircle2, FileText } from 'lucide-react';

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const kindBadge = (kind) => {
  const map = {
    'Item': 'bg-blue-100 text-blue-800',
    'Extra Labor': 'bg-purple-100 text-purple-800',
    'Field': 'bg-amber-100 text-amber-800',
  };
  return map[kind] || 'bg-gray-100 text-gray-800';
};

const ChangeRow = ({ c }) => {
  let icon, text, ring;
  if (c.action === 'added') {
    icon = <Plus className="w-4 h-4 text-green-600" />;
    ring = 'border-green-200 bg-green-50';
    text = <span><strong>Adicionado:</strong> {c.description} — Qtd <strong>{c.new_qty}</strong>{c.unit_price ? ` (${fmtMoney(c.unit_price)}/un)` : ''}</span>;
  } else if (c.action === 'removed') {
    icon = <Minus className="w-4 h-4 text-red-600" />;
    ring = 'border-red-200 bg-red-50';
    text = <span><strong>Removido:</strong> {c.description} — Qtd era <strong>{c.old_qty}</strong></span>;
  } else if (c.action === 'qty') {
    icon = <RefreshCw className="w-4 h-4 text-blue-600" />;
    ring = 'border-blue-200 bg-blue-50';
    text = <span><strong>{c.description}</strong>: Qtd <span className="line-through text-gray-500">{c.old_qty}</span> → <strong>{c.new_qty}</strong></span>;
  } else if (c.action === 'price') {
    icon = <DollarSign className="w-4 h-4 text-blue-600" />;
    ring = 'border-blue-200 bg-blue-50';
    text = <span><strong>{c.description}</strong>: Preço <span className="line-through text-gray-500">{fmtMoney(c.old_price)}</span> → <strong>{fmtMoney(c.new_price)}</strong></span>;
  } else {
    icon = <PencilLine className="w-4 h-4 text-amber-600" />;
    ring = 'border-amber-200 bg-amber-50';
    text = <span><strong>{c.description}</strong>: <span className="line-through text-gray-500">{c.old_value || '—'}</span> → <strong>{c.new_value || '—'}</strong></span>;
  }
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${ring}`} data-testid="fom-change-row">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 text-sm text-gray-800">{text}</div>
      <Badge className={`${kindBadge(c.kind)} text-[10px] shrink-0`}>{c.kind}</Badge>
    </div>
  );
};

const FomChangesModal = ({ open, onClose, data }) => {
  if (!data) return null;
  const changes = data.fom_changes || [];
  const hasChanges = !!data.has_changes && changes.length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fom-changes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasChanges
              ? (<><PencilLine className="w-5 h-5 text-emerald-600" /> Alterações do Supervisor</>)
              : (<><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Revisado sem alterações</>)}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Resumo do que o supervisor revisou e enviou ao Financeiro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 text-sm text-gray-600 border-b pb-3">
          <div><span className="text-gray-500">Labor Bill:</span> <strong className="text-gray-900">{data.job_name || '—'}</strong></div>
          {data.order_number ? <div><span className="text-gray-500">Ordem:</span> <strong className="text-gray-900">{data.order_number}</strong></div> : null}
          <div><span className="text-gray-500">Assinado por:</span> <strong className="text-gray-900">{data.signer_name || 'Supervisor'}</strong></div>
        </div>

        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 my-2">
          {hasChanges ? (
            <>
              <div className="text-center flex-1">
                <div className="text-[11px] text-gray-500 uppercase">Total anterior</div>
                <div className="text-base font-semibold text-gray-500 line-through">{fmtMoney(data.old_total)}</div>
              </div>
              <div className="text-gray-400">→</div>
              <div className="text-center flex-1">
                <div className="text-[11px] text-gray-500 uppercase">Novo total</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="fom-new-total">{fmtMoney(data.new_total)}</div>
              </div>
            </>
          ) : (
            <div className="text-center w-full">
              <div className="text-[11px] text-gray-500 uppercase">Total</div>
              <div className="text-lg font-bold text-emerald-700">{fmtMoney(data.new_total)}</div>
            </div>
          )}
        </div>

        {hasChanges ? (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> {changes.length} alteração(ões)
            </div>
            {changes.map((c, i) => <ChangeRow key={i} c={c} />)}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-6 text-sm">
            Nada foi alterado — o Supervisor aprovou e enviou como recebido.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FomChangesModal;
